import { useEffect, useState, useCallback } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
} from "@mui/material";
import { Transition } from "../../pages/app/utils/animation";
import {
  useEditorPreferences,
  useEditorSelection,
} from "../../store/editor/hooks";
import { useAnnotations } from "../../store/annotations/hooks";
import { useModals } from "../../store/dialogs/hooks";

const defaultValues = {
  audioStart: 1,
  audioEnd: 2,
  referenceChannel: 1,
  sampleRate: 44100,
  numSampleSyncWindow: 2,
  micSpacing: 40,
};

export const SyncDialog = ({
  clipLength,
  sampleRate,
  numberOfChannels,
  onRun,
}) => {
  const { annotations: regions } = useAnnotations();
  const { selectedChannel } = useEditorPreferences();
  const { selectedTag } = useEditorSelection();
  const { closeSyncDialog, showSyncDialog } = useModals();

  const handleClose = () => {
    setFormValues(defaultValues);
    closeSyncDialog();
  };

  const handleRunFilter = () => {
    onRun && onRun(getFormValues());
    closeSyncDialog();
  };

  const getFormDefaults = useCallback(() => {
    const defaults = {
      ...defaultValues,
      sampleRate: sampleRate,
      referenceChannel: selectedChannel,
      numberOfChannels: numberOfChannels,
    };
    regions.forEach((region) => {
      if (region.tag === selectedTag) {
        defaults.audioStart = region.start;
        defaults.audioEnd = region.end;
      }
    });
    return defaults;
  }, [sampleRate, selectedChannel, numberOfChannels, regions, selectedTag]);

  const [formValues, setFormValues] = useState(getFormDefaults());

  useEffect(() => {
    setFormValues(getFormDefaults());
  }, [getFormDefaults, sampleRate, numberOfChannels, selectedTag, regions]);

  const getFormValues = () => {
    const timeSearchDurationSeconds =
      formValues.audioEnd - formValues.audioStart;
    const numSampleSyncWindow =
      formValues.timeSearchDurationSeconds * formValues.sampleRate;
    const formPostValues = {
      micSpacing: formValues.micSpacing,
      numberOfChannels: formValues.numberOfChannels,
      referenceChannel: formValues.referenceChannel,
      numSampleSyncWindow: numSampleSyncWindow,
      sampleRate: formValues.sampleRate,
      refTimeSeconds: formValues.audioStart,
      timeSearchDurationSeconds: timeSearchDurationSeconds,
      timeSearchWindowSeconds: formValues.numSampleSyncWindow,
    };
    return formPostValues;
  };

  return (
    <Dialog
      aria-hidden={!showSyncDialog}
      open={showSyncDialog}
      onClose={handleClose}
      closeAfterTransition={false}
      slots={{
        transition: Transition,
      }}
    >
      <DialogTitle>Align & Combine Settings</DialogTitle>
      <DialogContent dividers>
        <DialogContentText sx={{ mb: 1 }}>
          You have selected the annotation <strong>{selectedTag}</strong> for
          time offset alignment.
        </DialogContentText>
        <DialogContentText>
          If this is correct click Run to continue. Otherwise, click Cancel to
          select a different annotation.
        </DialogContentText>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleRunFilter} variant="contained">
          Run
        </Button>
      </DialogActions>
    </Dialog>
  );
};
